import React, { FormEvent, useEffect, useState } from 'react';

type AuthStatus = 'checking' | 'guest' | 'authenticated';

type Entry = {
  id: string;
  title: string;
  details: string;
  metric: number | null;
  createdAt: string;
};

async function readJsonResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.toLowerCase().includes('application/json')) {
    const preview = (await response.text()).trim().slice(0, 120);
    if (preview.startsWith('<')) {
      throw new Error('API returned HTML instead of JSON. Check your /api deployment routing.');
    }
    throw new Error('API returned a non-JSON response.');
  }

  return response.json() as Promise<T>;
}

const Dashboard: React.FC = () => {
  const [authStatus, setAuthStatus] = useState<AuthStatus>('checking');
  const [pin, setPin] = useState('');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [metric, setMetric] = useState('');
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadEntries = async () => {
    setLoadingEntries(true);
    try {
      const response = await fetch('/api/entries', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Could not load entries.');
      }

      const payload = await readJsonResponse<{ entries?: Entry[] }>(response);
      setEntries(Array.isArray(payload.entries) ? payload.entries : []);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Could not load entries.');
    } finally {
      setLoadingEntries(false);
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch('/api/auth/session', {
          credentials: 'include'
        });

        if (!response.ok) {
          setAuthStatus('guest');
          return;
        }

        setAuthStatus('authenticated');
        await loadEntries();
      } catch {
        setAuthStatus('guest');
      }
    };

    checkSession();
  }, []);

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (!pin.trim()) {
      setError('Enter your PIN code.');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ pin: pin.trim() })
      });

      if (!response.ok) {
        const payload = await readJsonResponse<{ error?: string }>(response).catch(() => null);
        throw new Error(payload?.error || 'PIN login failed.');
      }

      setPin('');
      setAuthStatus('authenticated');
      await loadEntries();
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'PIN login failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setError('');
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include'
    }).catch(() => null);
    setAuthStatus('guest');
    setEntries([]);
  };

  const handleAddEntry = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/entries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          title: title.trim(),
          details: details.trim(),
          metric: metric.trim()
        })
      });

      if (!response.ok) {
        const payload = await readJsonResponse<{ error?: string }>(response).catch(() => null);
        throw new Error(payload?.error || 'Could not save entry.');
      }

      setTitle('');
      setDetails('');
      setMetric('');
      await loadEntries();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Could not save entry.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEntry = async (id: string) => {
    setError('');
    const response = await fetch(`/api/entries/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (!response.ok) {
      const payload = await readJsonResponse<{ error?: string }>(response).catch(() => null);
      setError(payload?.error || 'Could not delete entry.');
      return;
    }

    setEntries((currentEntries) => currentEntries.filter((entry) => entry.id !== id));
  };

  if (authStatus === 'checking') {
    return <section className="py-16">Checking session...</section>;
  }

  if (authStatus === 'guest') {
    return (
      <section className="py-10 max-w-[480px] mx-auto">
        <h1 className="text-3xl mb-4">Dashboard Login</h1>
        <p className="text-gray-600 mb-6">
          Enter your PIN to access your private tracker.
        </p>
        <form className="space-y-4" onSubmit={handleLogin}>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={pin}
            onChange={(event) => setPin(event.target.value)}
            placeholder="PIN code"
            className="w-full border border-gray-300 rounded-lg px-4 py-3"
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-black text-white px-5 py-2.5 rounded-lg disabled:opacity-50"
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
        {error && <p className="text-red-600 mt-4">{error}</p>}
      </section>
    );
  }

  return (
    <section className="py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
        <div>
          <h1 className="text-3xl mb-1">Dashboard</h1>
          <p className="text-gray-600">Track your own items in a JSON-backed private log.</p>
        </div>
        <button
          onClick={handleLogout}
          className="self-start sm:self-auto border border-black px-4 py-2 rounded-lg hover:bg-black hover:text-white transition"
        >
          Logout
        </button>
      </div>

      <form onSubmit={handleAddEntry} className="space-y-3 border border-gray-200 p-4 rounded-xl mb-8">
        <h2 className="text-xl mb-1">Add Entry</h2>
        <input
          type="text"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Title (required)"
          className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
        />
        <textarea
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          placeholder="Details"
          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 min-h-[120px]"
        />
        <input
          type="number"
          value={metric}
          onChange={(event) => setMetric(event.target.value)}
          placeholder="Optional numeric metric"
          className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-black text-white px-5 py-2.5 rounded-lg disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : 'Save Entry'}
        </button>
      </form>

      {error && <p className="text-red-600 mb-4">{error}</p>}

      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="border-b border-gray-200 px-4 py-3 bg-gray-50">
          <h2 className="text-xl mb-0">Recent Entries</h2>
        </div>
        {loadingEntries ? (
          <p className="p-4 text-gray-600">Loading entries...</p>
        ) : entries.length === 0 ? (
          <p className="p-4 text-gray-600">No entries yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {entries.map((entry) => (
              <li key={entry.id} className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-lg mb-1">{entry.title}</h3>
                    {entry.details && <p className="text-gray-700">{entry.details}</p>}
                    <p className="text-sm text-gray-500 mt-2">
                      {new Date(entry.createdAt).toLocaleString()}
                      {entry.metric !== null && ` • Metric: ${entry.metric}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteEntry(entry.id)}
                    className="border border-red-500 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default Dashboard;
